<template>
  <div class="w-100 h-100 overflow-hidden bg-light d-flex flex-row">
    <!-- Main Map Area -->
    <div class="position-relative flex-grow-1">
      <MapToolsPanel
        @zoomToCurrentSelection="handleZoomToCurrentSelection"
        @goToAssignedIsland="handleGoToAssignedIsland"
      />
      <PacificMap ref="pacificMap" />
    </div>

    <!-- Permanent desktop sidebar -->
    <DesktopSidebar>
      <div
        v-if="urlParamError"
        class="alert alert-warning m-3 mb-0"
        role="alert"
      >
        {{ urlParamError }}
      </div>
      <IslandNavigationList
        v-if="!selectedItem"
        ref="desktopNavigationList"
        :islands="getIslands"
        :title="selectionTitle"
        sortBy="alphabetical"
        :assignedIslandId="assignedIslandId"
        @select="handleIslandSelect"
      />
      <SelectionDetails v-else @back="handleBackToList" />
    </DesktopSidebar>

    <!-- Mobile Bottom Offcanvas (md and below) -->
    <MobileSelectionOffcanvas
      ref="mobileOffcanvas"
      :show="showMobileOffcanvas"
      @close="handleCloseMobileOffcanvas"
      :title="selectionTitle"
    >
      <div
        v-if="urlParamError"
        class="alert alert-warning m-3 mb-0"
        role="alert"
      >
        {{ urlParamError }}
      </div>
      <IslandNavigationList
        v-if="!selectedItem"
        ref="mobileNavigationList"
        :islands="getIslands"
        sortBy="alphabetical"
        :assignedIslandId="assignedIslandId"
        @select="handleIslandSelect"
      />
      <SelectionDetails v-else @back="handleBackToList" />
    </MobileSelectionOffcanvas>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { IslandHeaders } from "@/types/index.ts";
import MapToolsPanel from "@/components/activity/MapToolsPanel.vue";
import DesktopSidebar from "@/components/activity/DesktopSidebar.vue";
import MobileSelectionOffcanvas from "@/components/activity/MobileSelectionOffcanvas.vue";
import SelectionDetails from "@/components/activity/SelectionDetails.vue";
import IslandNavigationList from "@/components/activity/IslandNavigationList.vue";
import PacificMap from "@/components/activity/PacificMap.vue";

const MOBILE_BREAKPOINT = 992;

export default {
  name: "ActivityView",
  components: {
    MapToolsPanel,
    DesktopSidebar,
    MobileSelectionOffcanvas,
    SelectionDetails,
    IslandNavigationList,
    PacificMap
  },
  data() {
    return {
      urlParamError: "",
      // Mobile offcanvas should be open by default to show instructions
      showMobileOffcanvas: window.innerWidth < MOBILE_BREAKPOINT
    };
  },
  computed: {
    ...mapGetters([
      "selectedItem",
      "getIslands",
      "initialEnrichmentComplete",
      "assignedIslandId"
    ]),
    selectionTitle() {
      if (!this.selectedItem) return "Explore the Pacific";
      // Prefer western/English name when available
      return (
        this.selectedItem.Name ||
        this.selectedItem[IslandHeaders.NAME] ||
        "Selected Island"
      );
    }
  },
  watch: {
    urlParamError: {
      handler(newError, oldError) {
        console.log("urlParamError changed", newError, oldError);
      }
    },
    $route: {
      handler(to) {
        // Only process URL params after initial enrichment is complete
        if (this.initialEnrichmentComplete && to.name === "Activity") {
          this.handleUrlParams(to.query);
        }
      },
      immediate: true
    },
    initialEnrichmentComplete(complete) {
      // When enrichment completes, check URL params if on Activity route
      if (complete && this.$route.name === "Activity") {
        this.handleUrlParams(this.$route.query);
      }
    },
    selectedItem: {
      handler(newItem, oldItem) {
        // Clear URL param error when selection changes
        this.urlParamError = "";

        // Open mobile offcanvas when item is selected on mobile
        if (window.innerWidth < MOBILE_BREAKPOINT) {
          this.showMobileOffcanvas = true;
          // Scroll to top when selection changes on mobile
          this.$nextTick(() => {
            this.$refs.mobileOffcanvas?.scrollToTop();
          });
        }

        // Update URL when selection changes (but not from URL params)
        if (newItem && newItem !== oldItem && this.initialEnrichmentComplete) {
          const islandId = newItem[IslandHeaders.ID];
          if (islandId) {
            // Only update if URL doesn't already match (avoid loops)
            // Check if this change came from URL params by comparing with current route
            const currentIslandId = this.$route.query.island;
            if (currentIslandId !== islandId) {
              // Selection changed from map click, update URL without report param
              this.updateUrlParams(islandId, null);
              // Reset report index to null
              this.selectReportByIndex(null);
            }
          }
        } else if (!newItem && oldItem) {
          // Selection cleared
          this.updateUrlParams(null, null);
        }
      }
    }
  },
  methods: {
    ...mapActions(["clearSelection", "selectItem", "selectReportByIndex"]),
    handleZoomToCurrentSelection() {
      // Call the zoom method on the PacificMap component
      if (this.$refs.pacificMap) {
        this.$refs.pacificMap.zoomToCurrentSelection();
      }
    },
    handleGoToAssignedIsland() {
      if (this.assignedIslandId) {
        // Select the assigned island
        this.selectItem(this.assignedIslandId);
        // Zoom to it after a short delay to ensure selection is processed
        this.$nextTick(() => {
          this.handleZoomToCurrentSelection();
        });
      }
    },
    handleCloseMobileOffcanvas() {
      // Close the mobile offcanvas when user clicks close button or backdrop
      this.showMobileOffcanvas = false;
      // Clear the island selection, just like the "back to list" button does
      this.clearSelection();
      this.updateUrlParams(null, null);
    },
    handleCloseSelection() {
      this.clearSelection();
      // Clear URL params when selection is cleared
      this.updateUrlParams(null, null);
    },
    /**
     * Handles island selection from the navigation list
     * @param {Object} island - The selected island object
     */
    handleIslandSelect(island) {
      const islandId = island[IslandHeaders.ID];
      if (islandId) {
        this.selectItem(islandId);
      }
    },
    /**
     * Handles back button click to return to the island list
     */
    handleBackToList() {
      this.clearSelection();
      this.updateUrlParams(null, null);
      // Focus the first button in the navigation list for accessibility
      this.$nextTick(() => {
        const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
        const navigationList = isMobile
          ? this.$refs.mobileNavigationList
          : this.$refs.desktopNavigationList;
        if (navigationList) {
          navigationList.focusFirstButton();
        }
      });
    },
    handleUrlParams(query) {
      const islandId = query.island;
      const reportIndex = query.reportIndex;

      const makeSelection = (islandId, reportIndex) => {
        this.selectItem(islandId);
        this.selectReportByIndex(reportIndex);
      };

      // Clear previous errors
      this.urlParamError = "";

      if (islandId) {
        // Find island by ID
        const island = this.getIslands.find(
          (i) => i[IslandHeaders.ID] === islandId
        );

        if (island) {
          // Select report index if provided, otherwise set to null
          if (
            reportIndex !== undefined &&
            reportIndex !== null &&
            reportIndex !== ""
          ) {
            const index = parseInt(reportIndex, 10);
            if (
              !Number.isNaN(index) &&
              index >= 0 &&
              island.Reports &&
              index < island.Reports.length
            ) {
              makeSelection(islandId, index);
            } else {
              // Invalid report index
              if (Number.isNaN(index) || index < 0) {
                console.log("Invalid report index", reportIndex);
                this.urlParamError = `Invalid report index "${reportIndex} parameter in URL".`;
              } else {
                console.log(
                  "Report index does not exist for this island",
                  index
                );
                this.urlParamError = `Report index ${index} does not exist for ${island[IslandHeaders.NAME]}.`;
              }
            }
          } else {
            // No report index in URL, set to null (will render first report if exists)
            makeSelection(islandId, null);
          }
        } else {
          // Invalid island ID
          this.urlParamError = `Island with ID "${islandId}" not found.`;
        }
      } else {
        // No island ID in URL, clear selection
        this.clearSelection();
      }
    },
    updateUrlParams(islandId, reportIndex) {
      const query = {};
      if (islandId) {
        query.island = islandId;
        if (
          reportIndex !== undefined &&
          reportIndex !== null &&
          reportIndex > 0
        ) {
          query.reportIndex = reportIndex.toString();
        }
      }
      this.$router.push({ query }).catch(() => {
        // Ignore navigation duplicate errors
      });
    }
  }
};
</script>

<style scoped>
/* All styling is now handled by Bootstrap utility classes */
</style>
